import { GetAction } from '@property.com.au/argonaut';
import { MOBILE_MAX } from '@property.com.au/ui-kit';
import {
  canonical,
  description,
  join,
  meta,
  preload,
  renderAction,
  temporaryRedirectAction,
  title,
  openGraph,
} from '@rea-argonaut/actions';
import HomePageScreen from '../screens/HomePageScreen';
import { getEnv } from '../../helpers/getEnv';
import { LoadingViewContextProvider } from '../../app-kit/contexts/LoadingViewContext/LoadingViewContext';
import { trackView } from '@rea-argonaut/analytics';
import { getHomePageDataGetter } from '../../helpers/analytics/getHomePageDataGetter';
import { getLoggedOutUserDataGetter } from '../../helpers/analytics/getLoggedOutUserDataGetter';
import { getCampaignData } from '@realestate.com.au/iglu-data';
import { accountQuery } from '../../graphql/accountQuery';
import { createUrqlClient } from '../../graphql/clientUrqlClient';
import ogImage from '../../assets/og-image.jpg';
import heroAvif from '../../assets/hero.avif';
import heroMobileAvif from '../../assets/hero-mobile.avif';

export const getAction: GetAction = async ({ user: { pcauid, countryCode, lockeId } }) => {
  // Validate user is logged in through account query, unnecessary if no lockeId is present
  if (lockeId) {
    const client = await createUrqlClient();
    const { data, error } = await client.query(accountQuery, {}).toPromise();

    // Throw error to display error screen to users and a return 500 response code
    if (error?.networkError) {
      throw Error('Could not successfully connect to Experience API');
    }

    // Logged in members will be redirected to the dashboard page
    if (data?.account?.lockeId) {
      return temporaryRedirectAction('/dashboard/');
    }
  }

  const pageTitle = 'Property Value and Property Market Research - property.com.au';
  const pageDescription =
    'Research any address in Australia. Find how much a property is worth and its history, including sold prices.';

  return renderAction(async () => {
    trackView([
      getHomePageDataGetter(),
      getLoggedOutUserDataGetter({
        isSignedIn: false,
        pcauid,
        countryCode,
      }),
      getCampaignData(),
    ]);

    return {
      getElements: async () => {
        return {
          Screen: (
            <LoadingViewContextProvider>
              <HomePageScreen />
            </LoadingViewContextProvider>
          ),
        };
      },

      getHead: async () =>
        join(
          preload({
            href: heroMobileAvif,
            as: 'image',
            type: 'image/avif',
            media: `(max-width: ${MOBILE_MAX}px)`,
          }),
          preload({
            href: heroAvif,
            as: 'image',
            type: 'image/avif',
            media: `(min-width: ${MOBILE_MAX + 1}px)`,
          }),
          title(pageTitle),
          description(pageDescription),
          canonical(getEnv.CANONICAL_DOMAIN()),
          openGraph({
            property: 'og:locale',
            content: 'en_AU',
          }),
          openGraph({
            property: 'og:type',
            content: 'website',
          }),
          openGraph({
            property: 'og:title',
            content: pageTitle,
          }),
          openGraph({
            property: 'og:description',
            content: pageDescription,
          }),
          openGraph({
            property: 'og:url',
            content: getEnv.CANONICAL_DOMAIN(),
          }),
          openGraph({
            property: 'og:site_name',
            content: 'property.com.au',
          }),
          openGraph({
            property: 'og:image',
            content: ogImage,
          }),
          meta({
            name: 'robots',
            content: 'index, follow, noodp',
          }),
        ),
    };
  });
};
